// S13 Notification preferences (root 6.14, REQ-18.3, REQ-18.4): immediate / digest / off for each kind
// that applies to the person; reporter updates are "always immediate"; the daily digest time; and
// "Connected apps" (REQ-11.4; poc CT-013): one row per live MCP connection (client, connected, last used),
// with Disconnect. Disconnecting the last connection to a client also withdraws the approval, so the next
// connect asks again.
import type { NotificationMode } from '@feedback/shared/constants/code-lists';
import type { NotificationPreferences, PutNotificationPreferencesBody } from '@feedback/shared/contracts/delivery';
import type { McpConnectionView } from '@feedback/shared/contracts/mcp';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useId, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import { PageHeader } from '@/components/PageHeader';
import { RelativeTime } from '@/components/RelativeTime';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { api, seg } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { loadSession } from '@/lib/session';

const MODES: { value: NotificationMode; label: string }[] = [
  { value: 'immediate', label: 'Immediate' },
  { value: 'digest', label: 'Digest' },
  { value: 'off', label: 'Off' },
];
const MINUTES_PER_STEP = 30;
const STEPS_PER_DAY = (24 * 60) / MINUTES_PER_STEP;
const DIGEST_TIMES = Array.from({ length: STEPS_PER_DAY }, (_, i) => {
  const minutes = i * MINUTES_PER_STEP;
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
});

const prefsKey = ['notification-preferences'];
const connectionsKey = ['mcp-connections'];

export const Route = createFileRoute('/notifications')({
  beforeLoad: ({ context, location }) => loadSession(context.queryClient, location.href),
  component: Notifications,
});

function Notifications() {
  return (
    <>
      <PageHeader
        title="Notifications"
        description="How the Feedback tool tells you about tickets, in Microsoft Teams."
      />
      <div className="flex max-w-3xl flex-col gap-6">
        <Preferences />
        <ConnectedApps />
      </div>
    </>
  );
}

function Preferences() {
  const id = useId();
  const queryClient = useQueryClient();
  const prefs = useQuery({
    queryKey: prefsKey,
    queryFn: () => api.get<NotificationPreferences>('/api/me/notification-preferences'),
  });
  const [modes, setModes] = useState<Record<string, NotificationMode>>({});
  const [digestTime, setDigestTime] = useState('');
  useEffect(() => {
    if (prefs.data) {
      setModes(Object.fromEntries(prefs.data.kinds.map((k) => [k.code, k.mode])));
      setDigestTime(prefs.data.digestTime);
    }
  }, [prefs.data]);
  const save = useMutation({
    mutationFn: (body: PutNotificationPreferencesBody) =>
      api.put<NotificationPreferences>('/api/me/notification-preferences', body),
    onSuccess: (data) => {
      queryClient.setQueryData(prefsKey, data);
      toast.success('Your notification preferences are saved');
    },
  });
  if (prefs.isPending) {
    return <LoadingState rows={4} label="Loading your preferences" />;
  }
  if (prefs.isError) {
    return <ErrorState error={prefs.error} onRetry={() => void prefs.refetch()} />;
  }
  const changeable = prefs.data.kinds.filter((k) => k.canTurnOff);
  const fixed = prefs.data.kinds.filter((k) => !k.canTurnOff);
  const dirty =
    digestTime !== prefs.data.digestTime ||
    changeable.some((k) => modes[k.code] !== undefined && modes[k.code] !== k.mode);
  const times = DIGEST_TIMES.includes(prefs.data.digestTime)
    ? DIGEST_TIMES
    : [...DIGEST_TIMES, prefs.data.digestTime].sort();
  return (
    <Card>
      <CardContent className="pt-4">
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate({
              kinds: changeable.map((k) => ({ code: k.code, mode: modes[k.code] ?? k.mode })),
              ...(digestTime !== prefs.data.digestTime ? { digestTime } : {}),
            });
          }}
        >
          <table className="w-full text-sm">
            <caption className="sr-only">How you’re told about each kind of event</caption>
            <thead>
              <tr className="text-left text-xs text-muted">
                <th scope="col" className="py-2 font-medium">
                  Event
                </th>
                {MODES.map((m) => (
                  <th key={m.value} scope="col" className="w-12 py-2 text-center font-medium sm:w-20">
                    {m.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {changeable.map((kind) => (
                <tr key={kind.code} className="border-t border-border">
                  <th scope="row" className="py-2 pr-2 text-left font-normal">
                    {kind.label}
                  </th>
                  {MODES.map((m) => (
                    <td key={m.value} className="py-2 text-center">
                      <input
                        type="radio"
                        name={`${id}-${kind.code}`}
                        value={m.value}
                        checked={(modes[kind.code] ?? kind.mode) === m.value}
                        onChange={() => setModes((prev) => ({ ...prev, [kind.code]: m.value }))}
                        aria-label={`${kind.label}: ${m.label}`}
                        className="size-4 accent-[var(--fb-color-primary)]"
                      />
                    </td>
                  ))}
                </tr>
              ))}
              {fixed.map((kind) => (
                <tr key={kind.code} className="border-t border-border">
                  <th scope="row" className="py-2 pr-2 text-left font-normal">
                    {kind.label}
                  </th>
                  <td colSpan={MODES.length} className="py-2 text-center text-muted">
                    Always immediate, can’t be turned off
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-digest`}>Daily digest at</Label>
              <NativeSelect
                id={`${id}-digest`}
                className="w-32"
                value={digestTime}
                onChange={(e) => setDigestTime(e.target.value)}
              >
                {times.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <p className="pb-2 text-sm text-muted">Time zone: {prefs.data.timeZone}</p>
            <Button type="submit" className="ml-auto" disabled={!dirty || save.isPending}>
              Save preferences
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function ConnectedApps() {
  const queryClient = useQueryClient();
  const connections = useQuery({
    queryKey: connectionsKey,
    queryFn: async () => (await api.get<{ items: McpConnectionView[] }>('/api/me/mcp-connections')).items,
  });
  const [ending, setEnding] = useState<McpConnectionView | null>(null);
  const disconnect = useMutation({
    mutationFn: (connection: McpConnectionView) => api.delete(`/api/me/mcp-connections/${seg(connection.id)}`),
    onSuccess: (_data, connection) => {
      toast.success(`${connection.client.name} is disconnected`);
      void queryClient.invalidateQueries({ queryKey: connectionsKey });
    },
    onSettled: () => setEnding(null),
  });
  // The last connection to a client takes the approval with it: say so before it's ended.
  const isLastToClient =
    ending !== null && (connections.data ?? []).filter((c) => c.client.id === ending.client.id).length === 1;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Connected apps</CardTitle>
        <p className="text-sm text-muted">
          AI apps connected to work on Feedback tickets for you. They act as you, within your role.
        </p>
      </CardHeader>
      <CardContent>
        {connections.isPending ? (
          <LoadingState rows={2} />
        ) : connections.isError ? (
          <ErrorState error={connections.error} onRetry={() => void connections.refetch()} />
        ) : connections.data.length === 0 ? (
          <p className="text-sm text-muted">You haven’t connected any apps.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-border">
            {connections.data.map((connection) => (
              <li
                key={connection.id}
                data-testid="mcp-connection"
                className="flex flex-wrap items-center justify-between gap-2 py-2"
              >
                <div>
                  <p className="font-medium">{connection.client.name}</p>
                  <p className="text-sm text-muted">Connected {formatDateTime(connection.connectedAt)}</p>
                  <p className="text-sm text-muted">
                    Last used {connection.lastUsedAt ? <RelativeTime iso={connection.lastUsedAt} /> : 'not yet'}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setEnding(connection)}
                  aria-label={`Disconnect ${connection.client.name}`}
                >
                  Disconnect
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <ConfirmDialog
        open={ending !== null}
        onOpenChange={(open) => (open ? null : setEnding(null))}
        title={`Disconnect ${ending?.client.name ?? 'this app'}?`}
        description={
          isLastToClient
            ? 'It stops working on tickets for you at once. Connecting it again asks for your approval again.'
            : 'This connection stops working on tickets for you at once. Your other connections to it stay.'
        }
        confirmLabel="Disconnect"
        destructive
        pending={disconnect.isPending}
        onConfirm={() => (ending ? disconnect.mutate(ending) : undefined)}
      />
    </Card>
  );
}
